import {useEffect, useState} from 'react';
import {Link} from 'react-router';

import Button from '@/shared/components/button';
import Switch from '@/shared/components/Switch';
import {APP_ROUTES} from '@/utils_legacy/routes';

import {COOKIE_SECTIONS} from '../consts';
import {
  CookieConsentPreferencesProps,
  CookiePreferences,
  CookieSectionKey,
  ExpandedSections,
} from '../types';

const INITIAL_EXPANDED: ExpandedSections = {
  essential: false,
  statistics: false,
  marketing: false,
};

const CookieConsentPreferences = ({
  initialPreferences,
  onAcceptAll,
  onRejectNonEssential,
  onSavePreferences,
}: CookieConsentPreferencesProps) => {
  const [expandedSections, setExpandedSections] =
    useState<ExpandedSections>(INITIAL_EXPANDED);
  const [preferences, setPreferences] =
    useState<CookiePreferences>(initialPreferences);

  useEffect(() => {
    setExpandedSections(INITIAL_EXPANDED);
    setPreferences(initialPreferences);
  }, [initialPreferences]);

  const toggleSection = (key: CookieSectionKey): void => {
    setExpandedSections((prevState) => ({
      ...prevState,
      [key]: !prevState[key],
    }));
  };

  const togglePreference = (
    key: CookieSectionKey,
    isChecked: boolean
  ): void => {
    if (key === 'essential') {
      return;
    }

    setPreferences((prevState) => ({
      ...prevState,
      [key]: isChecked,
    }));
  };

  const getIsChecked = (key: CookieSectionKey): boolean => {
    if (key === 'essential') {
      return true;
    }

    return preferences[key];
  };

  return (
    <div className="flex max-h-[inherit] min-h-0 flex-col rounded-2lg bg-white px-7 py-5 pt-[52px] pr-5">
      <div className="min-h-0 flex-1 overflow-y-auto pr-2">
        <h4 className="mb-4 text-center font-semibold text-black text-lg">
          Privacy Preference Center
        </h4>
        <p className="mb-7 text-base">
          When you visit any web site, it may store or retrieve information on
          your browser, mostly in the form of cookies. This information might be
          about you, your preferences or your device. We have provided a simple
          tool below to accept or refuse them as you wish.&nbsp;
          <Link
            target="_blank"
            rel="noopener noreferrer"
            className="font-normal text-system-blue underline"
            to={APP_ROUTES.PrivacyPolicyGroup}
          >
            Privacy Policy
          </Link>
        </p>
        <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Button variant="system" onClick={onAcceptAll}>
            Accept all
          </Button>
        </div>
        <h4 className="text-center font-semibold text-black text-lg">
          Customize preferences
        </h4>
        <div className="my-4 flex flex-col gap-3">
          {COOKIE_SECTIONS.map((section) => {
            const isExpanded = expandedSections[section.key];
            const checked = getIsChecked(section.key);

            return (
              <div
                key={section.key}
                className="overflow-hidden rounded-2lg border border-inactive"
              >
                <div className="flex items-center justify-between gap-4 p-3">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    onClick={() => toggleSection(section.key)}
                    aria-expanded={isExpanded}
                  >
                    <span
                      className="inline-block font-light text-4xl leading-[0]"
                      aria-hidden
                    >
                      {isExpanded ? '−' : '+'}
                    </span>
                    <span className="truncate font-semibold text-black text-sm">
                      {section.title}
                    </span>
                  </button>
                  <Switch
                    checked={checked}
                    disabled={section.required}
                    onCheckedChange={(isChecked) =>
                      togglePreference(section.key, isChecked)
                    }
                  />
                </div>
                {isExpanded && (
                  <div className="bg-gray-light px-8 py-3 text-base">
                    {section.description}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div>
          <Link
            className="font-normal text-system-blue underline"
            to={APP_ROUTES.CookiePolicy}
            target="_blank"
            rel="noopener noreferrer"
          >
            Cookie Policy
          </Link>
        </div>
      </div>
      <div className="mt-6 pr-2">
        <div className="flex flex-col gap-3 sm:flex-row">
          <Button variant="system" onClick={onRejectNonEssential}>
            Reject non-essential
          </Button>
          <Button
            variant="system"
            onClick={() => onSavePreferences(preferences)}
          >
            Save preferences
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentPreferences;
